import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  parseFractionLikeInput as parseFraction,
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";
import ThalesTriangleDiagram, {
  ThalesCrossedDiagram,
} from "../../../../components/diagrams/ThalesTriangleDiagram";

const pick = (items) => items[Math.floor(Math.random() * items.length)];

/*
  Układ: wierzchołek O, dwa promienie, na nich AB ∥ CD.
  Górny promień:  O — B — D   (OB + BD = OD)
  Dolny promień:  O — A — C   (OA + AC = OC)
  Trójkąty OAB ~ OCD, więc:  OA/AC = OB/BD  oraz  OA/OC = OB/OD = AB/CD.

  Tryby:
  - singleSegment : trzy z czterech odcinków (OA, OB, AC, BD) dane, x = czwarty.
  - partWhole     : dana CAŁOŚĆ OD oraz OA, AC; szukamy części OB (część–całość).
  - similarEasy   : dane podstawy AB, CD oraz OA; szukamy całości OC (wprost ze stosunku).
  - similarHard   : dane podstawy AB, CD oraz AC; szukamy OA (prowadzi do równania).
*/

// Czwórki (OA, OB, AC, BD) z warunkiem OA·BD = OB·AC — dowolny z nich może być x.
const segmentQuads = [
  { OA: 6, OB: 4, AC: 15, BD: 10 },
  { OA: 5, OB: 3, AC: 15, BD: 9 },
  { OA: 8, OB: 5, AC: 24, BD: 15 },
  { OA: 9, OB: 6, AC: 21, BD: 14 },
  { OA: 6, OB: 9, AC: 4, BD: 6 },
  { OA: 4, OB: 6, AC: 6, BD: 9 },
  { OA: 10, OB: 6, AC: 15, BD: 9 },
  { OA: 12, OB: 8, AC: 9, BD: 6 },
  { OA: 6, OB: 8, AC: 9, BD: 12 },
];
const SEGMENT_SLOTS = ["OA", "OB", "AC", "BD"];

// Układ krzyżowy: k ∥ l oraz OA/OC = OD/OB. Każdy z czterech odcinków
// może być niewiadomą, a dane są dobrane tak, aby wynik był całkowity.
const crossedSegmentQuads = [
  { OA: 20, OC: 16, OD: 10, OB: 8 },
  { OA: 15, OC: 10, OD: 9, OB: 6 },
  { OA: 18, OC: 12, OD: 12, OB: 8 },
  { OA: 12, OC: 18, OD: 8, OB: 12 },
  { OA: 14, OC: 21, OD: 8, OB: 12 },
  { OA: 16, OC: 20, OD: 12, OB: 15 },
];
const CROSSED_SLOTS = ["OA", "OC", "OD", "OB"];

// OB = OD·OA / (OA + AC), wszystkie wartości całkowite.
const partWholeVariants = [
  { OD: 16, OA: 6, AC: 18 },
  { OD: 18, OA: 5, AC: 10 },
  { OD: 21, OA: 8, AC: 20 },
  { OD: 24, OA: 9, AC: 15 },
  { OD: 20, OA: 6, AC: 9 },
  { OD: 15, OA: 4, AC: 8 },
  { OD: 28, OA: 6, AC: 15 },
  { OD: 30, OA: 5, AC: 20 },
  { OD: 18, OA: 8, AC: 10 },
];

// OC = OA·CD / AB, całkowite (CD > AB).
const similarEasyVariants = [
  { AB: 4, CD: 6, OA: 6 },
  { AB: 3, CD: 5, OA: 6 },
  { AB: 5, CD: 8, OA: 10 },
  { AB: 6, CD: 9, OA: 8 },
  { AB: 4, CD: 7, OA: 8 },
  { AB: 6, CD: 10, OA: 9 },
];

// OA = AB·AC / (CD − AB), całkowite (CD > AB).
const similarHardVariants = [
  { AB: 4, CD: 6, AC: 9 },
  { AB: 6, CD: 9, AC: 8 },
  { AB: 5, CD: 8, AC: 9 },
  { AB: 4, CD: 7, AC: 9 },
  { AB: 6, CD: 10, AC: 10 },
  { AB: 8, CD: 12, AC: 9 },
  { AB: 3, CD: 5, AC: 8 },
];

// Niewiadoma = podstawa (AB lub CD). Czworki z |AB|/|CD| = |OA|/|OC| (AB·OC = CD·OA).
const missingBaseQuads = [
  { OA: 6, OC: 9, AB: 4, CD: 6 },
  { OA: 6, OC: 15, AB: 4, CD: 10 },
  { OA: 8, OC: 12, AB: 6, CD: 9 },
  { OA: 6, OC: 10, AB: 9, CD: 15 },
  { OA: 4, OC: 6, AB: 6, CD: 9 },
  { OA: 6, OC: 8, AB: 9, CD: 12 },
  { OA: 4, OC: 7, AB: 8, CD: 14 },
  { OA: 6, OC: 9, AB: 8, CD: 12 },
];
const BASE_SLOTS = ["AB", "CD"];

const TYPE_BY_MODE = {
  singleSegment: "FindThalesSegmentTask",
  crossedSegment: "FindThalesCrossedPreparationTask",
  partWhole: "FindThalesPartWholeTask",
  similarEasy: "FindSimilarBasesEasyTask",
  similarHard: "FindSimilarBasesHardTask",
  missingBase: "FindSimilarMissingBaseTask",
  talesPreparationMixed: "FindThalesPreparationMixedTask",
  similarPreparationMixed: "FindSimilarPreparationMixedTask",
};
const MODE_ORDER = ["singleSegment", "partWhole", "similarEasy", "similarHard", "missingBase"];

const TAGS = ["planimetria", "trojkaty", "tales", "podobienstwo"];

function parseAnswer(raw) {
  return parseFraction(String(raw || "").replace(/[^\d.,/-]+/g, ""));
}

/* ----------------------------- budowanie danych ----------------------------- */

function buildSingleSegment() {
  const q = pick(segmentQuads);
  const unknown = pick(SEGMENT_SLOTS);
  const show = (slot) => (slot === unknown ? "x" : String(q[slot]));
  return {
    mode: "singleSegment",
    ...q,
    unknown,
    answer: q[unknown],
    nearRatio: q.OB / (q.OB + q.BD),
    labels: {
      topNear: show("OB"),
      topFar: show("BD"),
      bottomNear: show("OA"),
      bottomFar: show("AC"),
    },
  };
}

function buildCrossedSegment() {
  const q = pick(crossedSegmentQuads);
  const unknown = pick(CROSSED_SLOTS);
  return {
    mode: "crossedSegment",
    ...q,
    unknown,
    answer: q[unknown],
    labels: Object.fromEntries(
      CROSSED_SLOTS.map((slot) => [slot, slot === unknown ? "x" : String(q[slot])])
    ),
  };
}

function buildMixedSegment() {
  return Math.random() < 0.5 ? buildSingleSegment() : buildCrossedSegment();
}

function buildPartWhole() {
  const v = pick(partWholeVariants);
  const OC = v.OA + v.AC;
  const answer = (v.OD * v.OA) / OC;
  return {
    mode: "partWhole",
    OD: v.OD,
    OA: v.OA,
    AC: v.AC,
    OC,
    answer,
    nearRatio: answer / v.OD,
    labels: { topNear: "x", bottomNear: String(v.OA), bottomFar: String(v.AC) },
    upperBrace: String(v.OD),
  };
}

function buildSimilarEasy() {
  const v = pick(similarEasyVariants);
  const answer = (v.OA * v.CD) / v.AB; // OC
  return {
    mode: "similarEasy",
    AB: v.AB,
    CD: v.CD,
    OA: v.OA,
    answer,
    nearRatio: v.AB / v.CD,
    labels: { bottomNear: String(v.OA) },
    lowerBrace: "x",
    chordLabels: { ab: String(v.AB), cd: String(v.CD) },
  };
}

function buildSimilarHard() {
  const v = pick(similarHardVariants);
  const answer = (v.AB * v.AC) / (v.CD - v.AB); // OA
  return {
    mode: "similarHard",
    AB: v.AB,
    CD: v.CD,
    AC: v.AC,
    answer,
    nearRatio: v.AB / v.CD,
    labels: { bottomNear: "x", bottomFar: String(v.AC) },
    chordLabels: { ab: String(v.AB), cd: String(v.CD) },
  };
}

function buildMissingBase() {
  const q = pick(missingBaseQuads);
  const unknown = pick(BASE_SLOTS);
  const show = (slot) => (slot === unknown ? "x" : String(q[slot]));
  return {
    mode: "missingBase",
    ...q,
    unknown,
    answer: q[unknown],
    nearRatio: q.OA / q.OC,
    labels: { bottomNear: String(q.OA) },
    lowerBrace: String(q.OC),
    chordLabels: { ab: show("AB"), cd: show("CD") },
  };
}

// W przygotowaniu jeden slot reprezentuje dwa pierwsze zadania kanoniczne,
// a drugi — dwa bardziej rozbudowane zadania z podobieństwa (4–5).
// Losowanie na poziomie grupy gwarantuje proporcję 50/50 niezależnie od liczby
// wariantów liczbowych wewnątrz poszczególnych typów.
function buildTalesPreparationMixed() {
  return Math.random() < 0.5 ? buildSingleSegment() : buildPartWhole();
}

function buildSimilarPreparationMixed() {
  return Math.random() < 0.5 ? buildSimilarHard() : buildMissingBase();
}

const BUILDERS = {
  segmentMixed: buildMixedSegment,
  singleSegment: buildSingleSegment,
  crossedSegment: buildCrossedSegment,
  partWhole: buildPartWhole,
  similarEasy: buildSimilarEasy,
  similarHard: buildSimilarHard,
  missingBase: buildMissingBase,
  talesPreparationMixed: buildTalesPreparationMixed,
  similarPreparationMixed: buildSimilarPreparationMixed,
};

/* --------------------------------- treść ---------------------------------- */

function mathList(items) {
  return items.map((m, i) => (
    <React.Fragment key={m}>
      {i > 0 ? (i === items.length - 1 ? " oraz " : ", ") : ""}
      <InlineMath math={m} />
    </React.Fragment>
  ));
}

function taskStatement(data) {
  if (data.mode === "crossedSegment") {
    const knowns = CROSSED_SLOTS
      .filter((slot) => slot !== data.unknown)
      .map((slot) => `${slot}=${data[slot]}`);
    return (
      <>
        Dwie sieczne przecinają się w punkcie <InlineMath math="O" />, a proste{" "}
        <InlineMath math="k" /> i <InlineMath math="l" /> są równoległe. Dane są długości{" "}
        {mathList(knowns)}. Długość odcinka <InlineMath math={data.unknown} /> oznaczamy przez{" "}
        <InlineMath math="x" />.
      </>
    );
  }

  if (data.mode === "singleSegment") {
    const knowns = SEGMENT_SLOTS.filter((s) => s !== data.unknown).map((s) => `${s}=${data[s]}`);
    return (
      <>
        Dane są długości {mathList(knowns)}. Długość odcinka{" "}
        <InlineMath math={data.unknown} /> oznaczamy przez <InlineMath math="x" />.
      </>
    );
  }

  if (data.mode === "partWhole") {
    return (
      <>
        Dane są długości {mathList([`OD=${data.OD}`, `OA=${data.OA}`, `AC=${data.AC}`])}. Punkt{" "}
        <InlineMath math="B" /> leży na odcinku <InlineMath math="OD" />, a długość{" "}
        <InlineMath math="OB" /> oznaczamy przez <InlineMath math="x" />.
      </>
    );
  }

  if (data.mode === "similarEasy") {
    return (
      <>
        Dane są długości podstaw {mathList([`AB=${data.AB}`, `CD=${data.CD}`])} oraz{" "}
        <InlineMath math={`OA=${data.OA}`} />. Długość odcinka <InlineMath math="OC" /> oznaczamy przez{" "}
        <InlineMath math="x" />.
      </>
    );
  }

  if (data.mode === "missingBase") {
    const knownBase = data.unknown === "AB" ? "CD" : "AB";
    return (
      <>
        Dane są długości {mathList([`OA=${data.OA}`, `OC=${data.OC}`])} oraz podstawa{" "}
        <InlineMath math={`${knownBase}=${data[knownBase]}`} />. Długość podstawy{" "}
        <InlineMath math={data.unknown} /> oznaczamy przez <InlineMath math="x" />. Obliczamy więc
        brakującą długość jednego z równoległych odcinków.
      </>
    );
  }

  return (
    <>
      Dane są długości podstaw {mathList([`AB=${data.AB}`, `CD=${data.CD}`])} oraz{" "}
      <InlineMath math={`AC=${data.AC}`} />. Długość odcinka <InlineMath math="OA" /> oznaczamy przez{" "}
      <InlineMath math="x" />.
    </>
  );
}

function Diagram({ data, showSimilarAngles = false }) {
  if (data.mode === "crossedSegment") {
    return <ThalesCrossedDiagram labels={data.labels} showSimilarAngles={showSimilarAngles} />;
  }
  return (
    <ThalesTriangleDiagram
      labels={data.labels}
      nearRatio={data.nearRatio}
      upperBrace={data.upperBrace}
      lowerBrace={data.lowerBrace}
      chordLabels={data.chordLabels}
      showSimilarAngles={showSimilarAngles}
    />
  );
}

/* ------------------------------- rozwiązanie ------------------------------- */

function explanationParts(data) {
  if (data.mode === "crossedSegment") {
    const v = (slot) => (slot === data.unknown ? "x" : String(data[slot]));
    let cross;
    let isolate;
    if (data.unknown === "OA") {
      cross = `${data.OB}x=${data.OC}\\cdot${data.OD}`;
      isolate = `x=\\dfrac{${data.OC * data.OD}}{${data.OB}}`;
    } else if (data.unknown === "OC") {
      cross = `${data.OD}x=${data.OA}\\cdot${data.OB}`;
      isolate = `x=\\dfrac{${data.OA * data.OB}}{${data.OD}}`;
    } else if (data.unknown === "OD") {
      cross = `${data.OC}x=${data.OA}\\cdot${data.OB}`;
      isolate = `x=\\dfrac{${data.OA * data.OB}}{${data.OC}}`;
    } else {
      cross = `${data.OA}x=${data.OC}\\cdot${data.OD}`;
      isolate = `x=\\dfrac{${data.OC * data.OD}}{${data.OA}}`;
    }
    return {
      step1: (
        <>
          Kąty przy <InlineMath math="O" /> są wierzchołkowe, a pozostałe pary
          równych kątów wynikają z równoległości prostych <InlineMath math="k\\parallel l" />.
          Dlatego <InlineMath math="\\triangle AOD\\sim\\triangle COB" />.
        </>
      ),
      generalProp: `\\dfrac{|OA|}{|OC|}=\\dfrac{|OD|}{|OB|}`,
      ocNote: null,
      substituted: `\\dfrac{${v("OA")}}{${v("OC")}}=\\dfrac{${v("OD")}}{${v("OB")}}`,
      solveLines: [cross, isolate, `x=${data.answer}`],
    };
  }

  if (data.mode === "singleSegment") {
    const { OA, OB, AC, BD, unknown, answer } = data;
    const v = (slot) => (slot === unknown ? "x" : String(data[slot]));
    let cross;
    let isolate;
    if (unknown === "AC") {
      cross = `${OB}\\cdot x=${OA}\\cdot ${BD}`;
      isolate = `x=\\dfrac{${OA * BD}}{${OB}}`;
    } else if (unknown === "BD") {
      cross = `${OA}\\cdot x=${OB}\\cdot ${AC}`;
      isolate = `x=\\dfrac{${OB * AC}}{${OA}}`;
    } else if (unknown === "OA") {
      cross = `${BD}\\cdot x=${OB}\\cdot ${AC}`;
      isolate = `x=\\dfrac{${OB * AC}}{${BD}}`;
    } else {
      cross = `${AC}\\cdot x=${OA}\\cdot ${BD}`;
      isolate = `x=\\dfrac{${OA * BD}}{${AC}}`;
    }
    return {
      step1: (
        <>
          Proste <InlineMath math={String.raw`AB\parallel CD`} /> przecinają ramiona kąta o wierzchołku{" "}
          <InlineMath math="O" />, więc odcinki wyznaczone na ramionach są proporcjonalne:
        </>
      ),
      generalProp: `\\dfrac{|OA|}{|AC|}=\\dfrac{|OB|}{|BD|}`,
      ocNote: null,
      substituted: `\\dfrac{${v("OA")}}{${v("AC")}}=\\dfrac{${v("OB")}}{${v("BD")}}`,
      solveLines: [cross, isolate, `x=${answer}`],
    };
  }

  if (data.mode === "partWhole") {
    return {
      step1: (
        <>
          Z podobieństwa <InlineMath math={String.raw`\triangle OAB\sim\triangle OCD`} /> korzystamy ze stosunku{" "}
          <em>części do całości</em>:
        </>
      ),
      generalProp: `\\dfrac{|OB|}{|OD|}=\\dfrac{|OA|}{|OC|}`,
      ocNote: `|OC|=|OA|+|AC|=${data.OA}+${data.AC}=${data.OC}`,
      substituted: `\\dfrac{x}{${data.OD}}=\\dfrac{${data.OA}}{${data.OC}}`,
      solveLines: [
        `${data.OC}\\cdot x=${data.OD}\\cdot ${data.OA}`,
        `x=\\dfrac{${data.OD * data.OA}}{${data.OC}}`,
        `x=${data.answer}`,
      ],
    };
  }

  if (data.mode === "similarEasy") {
    return {
      step1: (
        <>
          Trójkąty <InlineMath math="OAB" /> i <InlineMath math="OCD" /> są podobne, a podstawy{" "}
          <InlineMath math="AB" /> i <InlineMath math="CD" /> to boki odpowiadające, więc stosunki
          boków są równe:
        </>
      ),
      generalProp: `\\dfrac{|OA|}{|OC|}=\\dfrac{|AB|}{|CD|}`,
      ocNote: null,
      substituted: `\\dfrac{${data.OA}}{x}=\\dfrac{${data.AB}}{${data.CD}}`,
      solveLines: [
        `${data.AB}\\cdot x=${data.CD}\\cdot ${data.OA}`,
        `x=\\dfrac{${data.CD * data.OA}}{${data.AB}}`,
        `x=${data.answer}`,
      ],
    };
  }

  if (data.mode === "missingBase") {
    const { OA, OC, AB, CD, unknown, answer } = data;
    const v = (slot) => (slot === unknown ? "x" : String(data[slot]));
    let cross;
    let isolate;
    if (unknown === "CD") {
      cross = `${OA}\\cdot x=${AB}\\cdot ${OC}`;
      isolate = `x=\\dfrac{${AB * OC}}{${OA}}`;
    } else {
      cross = `${OC}\\cdot x=${CD}\\cdot ${OA}`;
      isolate = `x=\\dfrac{${CD * OA}}{${OC}}`;
    }
    return {
      step1: (
        <>
          Trójkąty <InlineMath math="OAB" /> i <InlineMath math="OCD" /> są podobne, więc stosunek
          długości równoległych podstaw jest taki sam jak stosunek odpowiadających im ramion:
        </>
      ),
      generalProp: `\\dfrac{|AB|}{|CD|}=\\dfrac{|OA|}{|OC|}`,
      ocNote: null,
      substituted: `\\dfrac{${v("AB")}}{${v("CD")}}=\\dfrac{${OA}}{${OC}}`,
      solveLines: [cross, isolate, `x=${answer}`],
    };
  }

  // similarHard
  return {
    step1: (
      <>
        Trójkąty <InlineMath math="OAB" /> i <InlineMath math="OCD" /> są podobne, więc stosunki
        boków odpowiadających są równe:
      </>
    ),
    generalProp: `\\dfrac{|OA|}{|OC|}=\\dfrac{|AB|}{|CD|}`,
    ocNote: `|OC|=|OA|+|AC|=x+${data.AC}`,
    substituted: `\\dfrac{x}{x+${data.AC}}=\\dfrac{${data.AB}}{${data.CD}}`,
    solveLines: [
      `${data.CD}\\cdot x=${data.AB}\\,(x+${data.AC})`,
      `${data.CD}x=${data.AB}x+${data.AB * data.AC}`,
      `${data.CD - data.AB}x=${data.AB * data.AC}`,
      `x=\\dfrac{${data.AB * data.AC}}{${data.CD - data.AB}}=${data.answer}`,
    ],
  };
}

export function renderExplanation({ data }) {
  const { step1, generalProp, ocNote, substituted, solveLines } = explanationParts(data);
  return (
    <div style={{ textAlign: "left", lineHeight: 1.85 }}>
      <Diagram data={data} showSimilarAngles />
      <p><strong>Krok 1.</strong> {step1}</p>
      <BlockMath math={generalProp} />
      {ocNote ? <BlockMath math={ocNote} /> : null}
      <p><strong>Krok 2.</strong> Podstawiamy dane:</p>
      <BlockMath math={substituted} />
      <p><strong>Krok 3.</strong> Obliczenia — mnożymy na krzyż:</p>
      {solveLines.map((line) => <BlockMath key={line} math={line} />)}
      <p>
        <strong>Odpowiedź.</strong> <InlineMath math={`x=${data.answer}`} />.
      </p>
    </div>
  );
}

/* --------------------------------- render --------------------------------- */

function NumberInput({ userAnswer, onChange, disabled }) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  return (
    <FractionInput
      value={raw}
      onChange={(value) =>
        onChange({
          raw: value?.raw ?? "",
          parsed: parseAnswer(value?.raw ?? ""),
        })
      }
      disabled={disabled}
      label="x"
      placeholder="np. 15"
    />
  );
}

export function generateTask(options = {}) {
  const mode = options.mode && BUILDERS[options.mode] ? options.mode : pick(MODE_ORDER);
  return {
    type: options.type || TYPE_BY_MODE[mode] || "FindThalesSegmentTask",
    data: BUILDERS[mode](),
    tags: TAGS,
  };
}

export function generateTasks() {
  return MODE_ORDER.map((mode) =>
    generateTask({ mode, type: TYPE_BY_MODE[mode] })
  ).filter(Boolean);
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.65 }}>
      <p>
        {data.mode !== "crossedSegment" ? (
          <>Na rysunku proste <InlineMath math="AB" /> i <InlineMath math="CD" /> są równoległe. </>
        ) : null}
        {taskStatement(data)} Oblicz <InlineMath math="x" />.
      </p>
      <Diagram data={data} />
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return <NumberInput userAnswer={userAnswer} onChange={onChange} disabled={disabled} />;
}

export function checkAnswer({ data }, userAnswer) {
  const parsed =
    typeof userAnswer === "object" && Number.isFinite(userAnswer?.parsed)
      ? userAnswer.parsed
      : parseAnswer(typeof userAnswer === "string" ? userAnswer : userAnswer?.raw);
  return Number.isFinite(parsed) && Math.abs(parsed - data.answer) < 0.01;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`x=${data.answer}`} />;
}

export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  if (!raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={formatLatexPreview(raw, "x")} />;
}

const thalesMissingSegmentTask = {
  generateTask,
  generateTasks,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};

export default thalesMissingSegmentTask;
